<template>
    <OverlayLoader :show="loadModal" />

    <div class="container-fluid px-4">
        <BaseTable :rows="availabilityList" :columns="columns" searchable :selectable="false" :loading="loading"
            :pagination="pagination" @changePage="onChangePage" @updatePageSize="onUpdatePageSize"
            @searchChanged="onSearchChanged">
            <template #buttons>
                <button v-if="!readOnly" class="btn btn-sm btn-info" @click="addAvailability()">
                    <i class="material-icons text-lg text-white">add</i>
                    Add New Recurrence
                </button>
            </template>

            <template #col-recurrence="{ row }">
                <ul class="list-disc pl-4">
                    <li v-for="(line, idx) in getRecurrenceLines(row)" :key="idx">
                        {{ sanitizeLine(line) }}
                    </li>
                </ul>
            </template>

            <template #actions="{ row }">
                <button v-if="!readOnly" class="btn btn-sm my-0 py-0" @click="editAvailability(row.id)">
                    <i class="material-icons text-lg text-orange">edit</i>
                </button>
                <button v-if="readOnly" class="btn btn-sm my-0 py-0" disabled>
                    <i class="material-icons text-lg" style="color: #6c757d;">visibility</i>
                </button>
                <button v-if="!readOnly" class="btn btn-sm my-0 py-0" @click="openDeleteModal(row)">
                    <i class="material-icons text-lg text-danger">delete</i>
                </button>
            </template>
        </BaseTable>

        <DeleteModal v-model:show="showDeleteModal" :itemName="selectedRecurrence" @confirm="deleteSkill" />

        <ContentModal v-model="showModal" title="Add New Recurrence" @update:modelValue="onModalClose">
            <template #default>
                <AddNewRecurrenceForm ref="AddNewRecurrence" :details="store.getters.recurrenceDetails" :activityId="activityId" @saved="onSaved" @save-error="onSaveError"
                    @error="onSaveError" />
            </template>

            <template #footer>
                <MaterialButton type="button" variant="primary" class="px-4 my-0" :disabled="isSaving"
                    @click="handleSave">
                    <span v-if="isSaving" class="spinner-border spinner-border-sm me-2" role="status"
                        aria-hidden="true"></span>
                    <i v-else class="material-icons text-lg me-1">save</i>
                    {{ isSaving ? 'Saving...' : 'Save' }}
                </MaterialButton>
            </template>
        </ContentModal>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { useToast } from "vue-toast-notification";

import BaseTable from "@/components/Table/Table.vue";
import DeleteModal from "@/components/Modal/ConfirmationModal.vue";
import ContentModal from "@/components/Modal/ContentModal.vue";
import MaterialButton from "@/components/common/MaterialButton.vue";
import OverlayLoader from "@/components/Loader/OverlayLoader.vue";
import AddNewRecurrenceForm from "./AddNewRecurrence.vue";
import { getLocationId } from "@/utils/SessionDataHandler";

defineProps({
    readOnly: { type: Boolean, default: false }
});

const store = useStore();
const route = useRoute();
const toast = useToast();

const showModal = ref(false);
const showDeleteModal = ref(false);
const isSaving = ref(false);
const isDeleting = ref(false);
const selectedSkill = ref(null);
const AddNewRecurrence = ref(null);
const loadModal = ref(false);
const activityId = route.params.activityid
const loading = ref(true);
const searchTerm = ref("");

const availabilityList = computed(() => store.getters.recurrences || []);
const pagination = computed(() => store.getters.recurrencesPagination);

const columns = [
    { key: "id", label: "ID" },
    { key: "recurrencename", label: "Recurrence Name" },
    { key: "description", label: "Description" },
    { key: "recurrence", label: "Recurrence" },
];

// ✅ Add availability modal
function addAvailability() {
    showModal.value = true;
}

// ✅ Handle modal close - reset form
function onModalClose(isOpen) {
    if (!isOpen && AddNewRecurrence.value) {
        // Modal is closing, reset the form
        AddNewRecurrence.value.resetForm();
    }
}

// ✅ Handle save
async function handleSave() {
    if (!AddNewRecurrence.value) return;
    isSaving.value = true;

    try {
        // Call and await the form's saveAvailability method
        await AddNewRecurrence.value.saveAvailability();
    } catch (err) {
        console.error("Availability save failed:", err);
        toast.error("Failed to save availability", { position: "top-right" });
    } finally {
        // Re-enable button after save (success or failure)
        isSaving.value = false;
    }
}

function onSaved() {
    showModal.value = false;
    toast.success("Availability saved successfully", { position: "top-right" });

    // Reset form after successful save
    if (AddNewRecurrence.value) {
        AddNewRecurrence.value.resetForm();
    }

    fetchAvailability(); // refresh table
}

function onSaveError(err) {
    console.error("Availability save error event:", err);
    toast.error("Failed to save availability", { position: "top-right" });
}

function openDeleteModal(skill) {
    selectedSkill.value = skill;
    showDeleteModal.value = true;
}

async function editAvailability(recurrenceId) {
    if (!AddNewRecurrence.value) return;

    showModal.value = true;
    loadModal.value = true;

    try {
        await store.dispatch('getRecurrenceDetail', { activityId, recurrenceId });
    } catch (err) {
        console.error('Failed to fetch recurrence details', err);
        showModal.value = false;
    } finally {
        loadModal.value = false;
    }
}

async function deleteSkill() {
    if (!selectedSkill.value) return;

    isDeleting.value = true;
    const locationId = getLocationId();
    const recurrenceId = selectedSkill.value.id;

    try {
        const res = await store.dispatch('deleteRecurrence', {
            recurrenceId,
            locationId,
            activityId,
        });

        showDeleteModal.value = false;

        const ok = res && (res.returncode === 0);

        if (ok) {
            toast.success('Availability deleted', { position: 'top-right' });
        } else {
            toast.error(res?.message || 'Failed to delete availability', { position: 'top-right' });
        }

        await fetchAvailability(); // refresh table
    } catch (err) {
        console.error('Delete availability failed', err);
        toast.error('Failed to delete availability', { position: 'top-right' });
    } finally {
        isDeleting.value = false;
    }
}

// ✅ API Fetch with pagination & search
// async function fetchAvailability(page = 1, pageSize = pagination.value.pageSize || 10) {
//   const personId = Number(route.params.userid);
//   const locationId = getLocationId();

//   loading.value = true;
//   try {
//     await store.dispatch("fetchPersonAvailability", {
//       personId,
//       locationId,
//       search: searchTerm.value,
//       page,
//       pageSize,
//     });
//   } finally {
//     loading.value = false;
//   }
// }

async function fetchAvailability(pageNumber = 1, pageSize = pagination.value.pageSize || 10) {
    loading.value = true;
    await store.dispatch('fetchRecurrences', { activityId, pageNumber, pageSize });
    loading.value = false;
}

// ✅ Pagination handlers
const onChangePage = async (page) => {
    await fetchAvailability(page);
};

const onUpdatePageSize = async (size) => {
    await fetchAvailability(1, size);
};

// ✅ Search handler
const onSearchChanged = async (term) => {
    searchTerm.value = term;
    await fetchAvailability(1);
};

// ✅ Convert API recurrence HTML to an array of lines
function getRecurrenceLines(row) {
    const raw = row.recurrence;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(raw, "text/html");
        const liElements = doc.querySelectorAll("li");
        const lines = Array.from(liElements).map(el => sanitizeLine(el.textContent));
        return lines;
    } catch (e) {
        return raw.split("\n").map(line => sanitizeLine(line));
    }
}

function sanitizeLine(raw) {
    if (!raw) return "";
    try {
        return String(raw).trim().replace(/^\.\s*/, "");
    } catch (e) {
        return String(raw);
    }
}

const selectedRecurrence = computed(() =>
    selectedSkill.value && selectedSkill.value.name
        ? selectedSkill.value.name
        : ""
);

// 🧠 Initial Load
onMounted(async () => {
    await fetchAvailability();
});
</script>